import {
  Box,
  Button,
  Card,
  Fade,
  Grid,
  IconButton,
  InputAdornment,
  SelectChangeEvent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import moment from "moment";
import {
  IEnhancedTablePropsStudentAttendanceRecord,
  IStudentAttendanceData,
  IStudentAttendanceDispatch,
  IStudentAttendanceFilter,
  IStudentAttendanceListMeta,
  IStudentAttendanceListPagePayloadValues,
  IStudentAttendanceRecordDetails,
  IStudentAttendanceState,
} from "utils/interfaces/student";
import { useCallback, useEffect, useState } from "react";
import {
  BLENDED_FACE_TO_FACE_ONLINE,
  CLEAR_TOOLTIP_TITLE,
  DEFAULT_PAGE_NO,
  DEFAULT_PAGE_SIZE,
  MAX_INT_VALUE,
} from "utils/constants/constant";
import { useHistory, useLocation } from "react-router";
import { useSelector } from "react-redux";
import {
  allowSearch,
  base64ToArrayBuffer,
  getPagePayloadValuesWithId,
  getPagePermissions,
  hideLoaderForBatchApiCall,
  onTextFieldKeyPress,
  showLoader,
  showLoaderForBatchApiCall,
  toggleFilter,
} from "utils/helper";
import {
  GenerateCertificateSuccessPayload,
  StudentAttendanceListSuccessPayload,
} from "store/student/types";
import { AppPages } from "utils/enums/app-pages";
import NoRecords from "components/NoRecords";
import {
  closeIcon,
  filterLightIcon,
  filterPrimaryIcon,
  generateCertificate,
  printIcon,
} from "assets/images";
import { toast } from "react-toastify";
import { CERTIFICATE_GENERATED } from "utils/constants/Messages";
import { DownloadCertificateSuccessPayload } from "store/studentDashboard/types";
import CustomPagination from "components/customPagination";
import { Order, OrderByOptions } from "utils/enums/sorting";
import { DEFAULT_STUDENT_ATTENDANCE_FILTER } from "utils/constants/student";
import { ExportToCsv } from "export-to-csv";
import GenerateCertificatePopUp from "./GenerateCertificateModal";
import StudentAttendanceFilter from "./StudentAttendanceFilter";

const EnhancedTableHead = (
  props: IEnhancedTablePropsStudentAttendanceRecord
) => {
  const { order, orderBy, onRequestSort, fieldName, keyName, sxStyle } = props;
  const createSortHandler =
    (newOrderBy: keyof IStudentAttendanceRecordDetails) =>
    (event: React.MouseEvent<unknown>) => {
      onRequestSort(event, newOrderBy);
    };

  return (
    <TableCell
      key={keyName}
      align="left"
      onClick={createSortHandler(keyName)}
      sortDirection={orderBy === keyName ? order : false}
      sx={sxStyle}
      className="term-sorting"
    >
      {fieldName}
      <Box component="span" className="sorting-icon" />
    </TableCell>
  );
};

export type StudentAttendanceProps = IStudentAttendanceState &
  IStudentAttendanceDispatch;

const StudentAttendance: React.FC<StudentAttendanceProps> = (props) => {
  const { courseListRequest, termListRequest } = props;

  const [studentAttendanceList, setStudentAttendanceList] =
    useState<IStudentAttendanceData[]>();
  const [studentAttendanceListMeta, setStudentAttendanceListMeta] =
    useState<IStudentAttendanceListMeta>();

  const [page, setPage] = useState<string>(DEFAULT_PAGE_SIZE);
  const [pageNo, setPageNo] = useState<number>(DEFAULT_PAGE_NO);
  const [searchText, setSearchText] = useState<string>("");
  const history = useHistory();
  const user = useSelector((state: any) => state?.auth?.user);
  const location = useLocation<{ studentId: number; studentName: string }>();
  const id = location?.state?.studentId;
  const studentName = location?.state?.studentName;
  const [studentCourseEnrolmentId, setStudentCourseEnrolmentId] =
    useState<number>(0);
  const [isOpenConfirmationModal, setIsOpenConfirmationModal] =
    useState<boolean>(false);
  const [orderBy, setOrderBy] =
    useState<keyof IStudentAttendanceRecordDetails>("course");
  const [order, setOrder] = useState<Order>(OrderByOptions.Asc);
  const [filterBox, setFilterBox] = useState<boolean>(false);
  const [filteredData, setFilteredData] = useState<IStudentAttendanceFilter>(
    DEFAULT_STUDENT_ATTENDANCE_FILTER
  );
  const [appliedFilter, setAppliedFilter] = useState<boolean>(false);

  const handleChange = (event: SelectChangeEvent) => {
    setPage(event.target.value as string);
    setPageNo(1);
  };

  const onPageChange = (e: any, value: number) => {
    setPageNo(value);
  };

  const handleRequestSort = useCallback(
    (
      event: React.MouseEvent<unknown>,
      newOrderBy: keyof IStudentAttendanceRecordDetails
    ) => {
      const isAsc = orderBy === newOrderBy && order === OrderByOptions.Asc;
      const toggledOrder = isAsc ? OrderByOptions.Desc : OrderByOptions.Asc;
      setOrder(toggledOrder);
      setOrderBy(newOrderBy);
    },
    [order, orderBy]
  );

  const onStudentAttendanceListSuccess = (
    response: StudentAttendanceListSuccessPayload
  ) => {
    hideLoaderForBatchApiCall();
    setStudentAttendanceList(response.studentAttendanceList);
    setStudentAttendanceListMeta(response.studentAttendanceListPagination);
  };

  const getStudentAttendanceList = async (
    filterData: IStudentAttendanceFilter,
    filtered?: boolean,
    pageNum?: number
  ) => {
    const { studnetAttendanceListRequest } = props;

    if (studnetAttendanceListRequest) {
      showLoaderForBatchApiCall();
      const values: IStudentAttendanceListPagePayloadValues =
        getPagePayloadValuesWithId(id, orderBy, order, pageNo, page);
      values.name = filtered ? "" : searchText.trim();

      if (filterData.years?.length) values.years = filterData.years.map(Number);
      if (filterData.termIds?.length)
        values.termIds = filterData.termIds.map(Number);
      if (filterData.courseIds?.length)
        values.courseIds = filterData.courseIds.map(Number);
      if (filterData.studentAttendance?.length)
        values.studentAttendance = filterData.studentAttendance;

      if (filterData) {
        setFilteredData(filterData);
        setAppliedFilter(true);
        if (pageNum) {
          values.pageNo = pageNum;
          setPageNo(pageNum);
        }
      } else {
        setFilteredData(DEFAULT_STUDENT_ATTENDANCE_FILTER);
      }

      const payload = {
        values,
        callback: onStudentAttendanceListSuccess,
      };
      studnetAttendanceListRequest(payload);
    }
  };

  useEffect(() => {
    const permission = getPagePermissions(user?.permissions, AppPages.Student);
    if (permission?.view) {
      if (!appliedFilter)
        getStudentAttendanceList(filteredData, true, DEFAULT_PAGE_NO);
      else getStudentAttendanceList(filteredData);
    } else {
      history.goBack();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, pageNo, order, orderBy]);

  const handleStudentSearch = (event: any) => {
    setSearchText(event.target.value);
  };

  const handleClearSearch = () => {
    setSearchText("");
    getStudentAttendanceList(filteredData, true);
  };

  const handleStudentSearchKeyDown = (e: any) => {
    if (allowSearch(e)) {
      setPageNo(1);
      getStudentAttendanceList(filteredData);
    }
  };

  const handleGenerateCertificateModal = (enId: number) => {
    setStudentCourseEnrolmentId(enId);
    setIsOpenConfirmationModal(true);
  };

  const handleGenerateCertificateModalClose = () => {
    setIsOpenConfirmationModal(false);
    setStudentCourseEnrolmentId(0);
  };

  const onGenerateCertificateSuccess = (
    response: GenerateCertificateSuccessPayload
  ) => {
    if (response) {
      setIsOpenConfirmationModal(false);
      setStudentAttendanceList((prevList) =>
        prevList?.map((item) =>
          item.id === studentCourseEnrolmentId
            ? {
                ...item,
                isCertificateGenerated: item.isCertificateGenerated
                  ? item.isCertificateGenerated
                  : !item.isCertificateGenerated,
              }
            : item
        )
      );
      toast.success(CERTIFICATE_GENERATED);
    }
  };

  const handleApplyCourseYesClick = () => {
    const { generateCertificateRequest } = props;

    if (generateCertificateRequest) {
      showLoader();
      const payload = {
        values: {
          id: Number(studentCourseEnrolmentId),
        },
        callback: onGenerateCertificateSuccess,
      };

      generateCertificateRequest(payload);
    }
    setIsOpenConfirmationModal(false);
  };

  const onDownloadCertificateSuccess = (
    response: DownloadCertificateSuccessPayload
  ) => {
    const item = base64ToArrayBuffer(response.item);
    const file = new Blob([item], {
      type: "application/pdf",
    });

    const fileURL = URL.createObjectURL(file);

    const iframe = document.createElement("iframe");
    iframe.style.display = "none";
    iframe.style.setProperty("print", "fit-to-print");
    iframe.src = fileURL;
    document.body.appendChild(iframe);

    iframe.onload = () => {
      const win = iframe.contentWindow;
      if (win) {
        win.print();
      }
    };
  };

  const onDownloadCertificate = (courseEnrolmentId: number) => {
    const { downloadCertificateRequest } = props;

    if (downloadCertificateRequest) {
      showLoader();

      const payload = {
        values: { id: courseEnrolmentId },
        callback: onDownloadCertificateSuccess,
      };

      downloadCertificateRequest(payload);
    }
  };

  const handleFilterApplyClick = (formData: IStudentAttendanceFilter) => {
    setFilteredData(formData);
    setSearchText("");
    if (pageNo === DEFAULT_PAGE_NO) {
      getStudentAttendanceList(formData, true, DEFAULT_PAGE_NO);
    } else {
      setPageNo(DEFAULT_PAGE_NO);
    }
    setFilterBox(false);
    toggleFilter();
  };

  const csvFileHeaders = [
    "Course",
    "Term",
    "Year",
    "Location",
    "Date of First Session",
    "Time and duration of each session",
    "No. Of Session in this Course",
    "Attendance",
  ];

  const getAttendanceRecordModifiedData = (list: IStudentAttendanceData[]) => {
    const data = list?.map((record: IStudentAttendanceData) => {
      return {
        Course: record?.course,
        Term: record?.term,
        Year: record?.year,
        Location: `${record?.location}${
          record?.isBlended ? BLENDED_FACE_TO_FACE_ONLINE : ""
        }`,
        "Date of First Session": moment(record?.dateOfFirstSession).format(
          "DD MMM YYYY"
        ),
        "Time and duration of each session": `${moment(
          record?.startTimeOfSession
        ).format("h:mm A")} to ${moment(record?.endTimeOfSession).format(
          "h:mm A"
        )}`,
        "No. Of Session in this Course": record?.noOfSession,
        Attendance: record?.attendance,
      };
    });

    return data;
  };

  const onExportAttendanceRecordSuccess = (
    response: StudentAttendanceListSuccessPayload
  ) => {
    if (response.studentAttendanceList?.length) {
      const modifiedAttendanceRecordData = getAttendanceRecordModifiedData(
        response.studentAttendanceList
      );
      const exportOptions = {
        fieldSeparator: ",",
        quoteStrings: '"',
        decimalSeparator: ".",
        showLabels: true,
        useTextFile: false,
        useBom: true,
        headers: csvFileHeaders,
        filename: "Student-Attendance-Record",
      };

      const csvExporter = new ExportToCsv(exportOptions);
      csvExporter.generateCsv(modifiedAttendanceRecordData);
    }
    hideLoaderForBatchApiCall();
  };

  const getExportAttendanceRecord = () => {
    const { studnetAttendanceListRequest } = props;

    if (studnetAttendanceListRequest) {
      showLoaderForBatchApiCall();
      const values: IStudentAttendanceListPagePayloadValues =
        getPagePayloadValuesWithId(
          id,
          orderBy,
          order,
          DEFAULT_PAGE_NO,
          MAX_INT_VALUE
        );
      values.name = searchText.trim();

      if (filteredData.years?.length)
        values.years = filteredData.years.map(Number);
      if (filteredData.termIds?.length)
        values.termIds = filteredData.termIds.map(Number);
      if (filteredData.courseIds?.length)
        values.courseIds = filteredData.courseIds.map(Number);
      if (filteredData.studentAttendance?.length)
        values.studentAttendance = filteredData.studentAttendance;

      const payload = {
        values,
        callback: onExportAttendanceRecordSuccess,
      };
      studnetAttendanceListRequest(payload);
    }
  };

  return (
    <>
      <Box className="content-header">
        <Typography variant="h2" className="heading">
          Attendance record
        </Typography>
        <Box sx={{ display: "flex", columnGap: "15px" }}>
          <Button
            variant="outlined"
            className="btn-export"
            onClick={getExportAttendanceRecord}
            disabled={!studentAttendanceListMeta?.totalCount}
          >
            Export
          </Button>
          <Button
            variant="outlined"
            className="btn-filter"
            onClick={() => {
              setFilterBox(true);
              toggleFilter();
            }}
          >
            <img src={filterPrimaryIcon} alt="filter" className="default" />
            <img src={filterLightIcon} alt="filter" className="active" />
            <span>Filter</span>
          </Button>
          <Button
            variant="outlined"
            className="btn-back"
            onClick={() => {
              history.goBack();
            }}
          >
            Back
          </Button>
        </Box>
      </Box>
      <Grid
        container
        spacing={{ xs: "16px", lg: "20px", xl: "24px" }}
        className="content-container"
      >
        <Grid item xs={12}>
          <Card>
            <Box className="table-card-header">
              <Box
                sx={{ justifyContent: "space-between", width: "100%" }}
                className="search-filter-inputs"
              >
                <TextField
                  id="search-box"
                  variant="outlined"
                  className="search-input"
                  placeholder="Search"
                  onChange={handleStudentSearch}
                  onKeyDown={handleStudentSearchKeyDown}
                  value={searchText}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment
                        sx={{ position: "absolute", right: "0" }}
                        position="end"
                      >
                        {searchText && (
                          <Tooltip
                            TransitionComponent={Fade}
                            TransitionProps={{ timeout: 600 }}
                            title={CLEAR_TOOLTIP_TITLE}
                            arrow
                          >
                            <IconButton edge="end" onClick={handleClearSearch}>
                              <img src={closeIcon} alt="close" />
                            </IconButton>
                          </Tooltip>
                        )}
                      </InputAdornment>
                    ),
                  }}
                  onKeyPress={(event) =>
                    onTextFieldKeyPress(event, searchText?.length)
                  }
                />

                <Typography variant="h5">
                  Student Name : <span className="mail-add">{studentName}</span>
                </Typography>
              </Box>
            </Box>
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <EnhancedTableHead
                      order={order}
                      orderBy={orderBy}
                      onRequestSort={(e) => handleRequestSort(e, "course")}
                      fieldName="Course"
                      keyName="course"
                      sxStyle={{ width: "192px", minWidth: "192px" }}
                    />
                    <EnhancedTableHead
                      order={order}
                      orderBy={orderBy}
                      onRequestSort={(e) => handleRequestSort(e, "term")}
                      fieldName="Term"
                      keyName="term"
                      sxStyle={{ width: "110px", minWidth: "110px" }}
                    />
                    <TableCell
                      align="center"
                      sx={{ width: "90px", minWidth: "90px" }}
                    >
                      Year
                    </TableCell>
                    <TableCell
                      align="left"
                      sx={{ width: "232px", minWidth: "232px" }}
                    >
                      Location
                    </TableCell>
                    <EnhancedTableHead
                      order={order}
                      orderBy={orderBy}
                      onRequestSort={(e) =>
                        handleRequestSort(e, "dateOfFirstSession")
                      }
                      fieldName="Date of First Session"
                      keyName="dateOfFirstSession"
                      sxStyle={{ width: "184px", minWidth: "184px" }}
                    />
                    <TableCell
                      align="left"
                      sx={{ width: "179px", minWidth: "179px" }}
                    >
                      Time and duration of each session
                    </TableCell>
                    <TableCell
                      align="center"
                      sx={{ width: "123px", minWidth: "123px" }}
                    >
                      No. Of Session in this Course
                    </TableCell>
                    <EnhancedTableHead
                      order={order}
                      orderBy={orderBy}
                      onRequestSort={(e) => handleRequestSort(e, "attendance")}
                      fieldName="Attendance"
                      keyName="attendance"
                      sxStyle={{
                        minWidth: "127px",
                        width: "127px",
                        textAlign: "center",
                      }}
                    />
                    <TableCell
                      align="left"
                      sx={{ minWidth: "110px", width: "110px" }}
                    >
                      <span style={{ marginLeft: "10px" }}>Action</span>
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {studentAttendanceList?.map((row) => (
                    <TableRow>
                      <TableCell component="th" scope="row">
                        <Typography
                          variant="body2"
                          className="sco-list-word-wrap"
                        >
                          {row?.course}
                        </Typography>
                      </TableCell>
                      <TableCell>{row?.term}</TableCell>
                      <TableCell align="center">{row.year}</TableCell>
                      <TableCell>
                        <Typography
                          variant="body2"
                          className="sco-list-word-wrap"
                        >
                          {row?.location}
                          {row.isBlended && BLENDED_FACE_TO_FACE_ONLINE}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        {moment(row?.dateOfFirstSession).format("DD MMM YYYY")}
                      </TableCell>
                      <TableCell>
                        {moment(row?.startTimeOfSession).format("h:mm A")} to{" "}
                        {moment(row?.endTimeOfSession).format("h:mm A")}
                      </TableCell>
                      <TableCell align="center">{row?.noOfSession}</TableCell>
                      <TableCell
                        align="center"
                        className={`${row?.attendance.toLowerCase()} status`}
                      >
                        <div>{row.attendance}</div>
                      </TableCell>
                      <TableCell
                        align="left"
                        className={`${row?.attendance.toLowerCase()} status`}
                      >
                        <Tooltip
                          TransitionComponent={Fade}
                          TransitionProps={{ timeout: 600 }}
                          title={
                            row?.isCertificateGenerated
                              ? "Certificate Generated"
                              : "Generate Certificate"
                          }
                          arrow
                        >
                          <IconButton
                            onClick={() =>
                              row?.isCertificateGenerated
                                ? null
                                : handleGenerateCertificateModal(row.id)
                            }
                            className={
                              row?.isCertificateGenerated
                                ? "disabled-icon"
                                : "download-btn"
                            }
                          >
                            <img
                              src={generateCertificate}
                              alt="generate-certificated"
                            />
                          </IconButton>
                        </Tooltip>
                        {row.isCertificateGenerated && (
                          <Tooltip
                            TransitionComponent={Fade}
                            TransitionProps={{ timeout: 600 }}
                            title="Print Certificate"
                            arrow
                          >
                            <IconButton
                              onClick={() => onDownloadCertificate(row.id)}
                              className="download-btn"
                            >
                              <img src={printIcon} alt="print" />
                            </IconButton>
                          </Tooltip>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
            {studentAttendanceList && studentAttendanceList.length > 0 ? (
              <CustomPagination
                handleChange={handleChange}
                page={page}
                pageNo={pageNo}
                onPageChange={onPageChange}
                listMeta={studentAttendanceListMeta}
              />
            ) : (
              <NoRecords />
            )}
          </Card>
          <GenerateCertificatePopUp
            handleYesClick={handleApplyCourseYesClick}
            isOpenConfirmationModal={isOpenConfirmationModal}
            handleGenerateCertificateModalClose={
              handleGenerateCertificateModalClose
            }
          />
        </Grid>
      </Grid>
      {filterBox && (
        <StudentAttendanceFilter
          filterBox={filterBox}
          setFilterBox={setFilterBox}
          handleApplyClick={handleFilterApplyClick}
          filteredData={filteredData}
          setFilteredData={setFilteredData}
          pageNo={pageNo}
          setPageNo={setPageNo}
          setAppliedFilter={setAppliedFilter}
          getStudentAttendanceList={getStudentAttendanceList}
          courseListRequest={courseListRequest}
          termListRequest={termListRequest}
        />
      )}
    </>
  );
};

export default StudentAttendance;
